// Reference data for the signup onboarding wizard. Lives in interfaces so the
// canonical lists (module keys, governorates) are shared between the wizard,
// the data-service create_tenant call, the module-gated sidebar, and any
// future client/supplier forms that want a governorate dropdown.

/* ----------------------- Modules ----------------------- */

/** Canonical module keys. Mirror the rows we INSERT into `module_settings`
 *  on tenant create, and the keys the sidebar consults to show/hide groups. */
export const MODULE_KEYS = [
  'sales',
  'purchases',
  'stock',
  'payments',
  'treasury',
  'checks',
  'withholding',
  'storefront',
  'caisse',
  'pro',
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

/** Modules that are always-on for every tenant — they are the core of the
 *  product (you can't sell anything without sales + articles). */
export const ALWAYS_ON_MODULES: readonly ModuleKey[] = ['sales'];

/** Default opt-in selection for a brand-new tenant. Picked to match what most
 *  Tunisian SMBs need on day one; everything else they activate later. */
export const DEFAULT_ENABLED_MODULES: readonly ModuleKey[] = [
  'sales',
  'purchases',
  'stock',
  'payments',
];

/** Catalog entry shown to the user in the wizard's module step. Translation
 *  keys point at libs/i18n/.../onboarding.modules.<key>.{title,desc}. */
export interface ModuleCatalogEntry {
  key: ModuleKey;
  /** Tabler icon name (consumed by `@/components/icons` `<DynamicIcon>`). */
  icon: string;
  /** Translation key suffix under `onboarding.modules`. */
  i18nKey: string;
  /** Whether the user can disable it in the wizard. */
  alwaysOn: boolean;
}

export const MODULE_CATALOG: readonly ModuleCatalogEntry[] = [
  { key: 'sales',       icon: 'shopping-cart', i18nKey: 'sales',       alwaysOn: true  },
  { key: 'purchases',   icon: 'shopping-bag',  i18nKey: 'purchases',   alwaysOn: false },
  { key: 'stock',       icon: 'warehouse',     i18nKey: 'stock',       alwaysOn: false },
  { key: 'payments',    icon: 'banknote',      i18nKey: 'payments',    alwaysOn: false },
  { key: 'treasury',    icon: 'landmark',      i18nKey: 'treasury',    alwaysOn: false },
  { key: 'checks',      icon: 'book-check',    i18nKey: 'checks',      alwaysOn: false },
  { key: 'withholding', icon: 'file-text',     i18nKey: 'withholding', alwaysOn: false },
  { key: 'storefront',  icon: 'globe',         i18nKey: 'storefront',  alwaysOn: false },
  { key: 'caisse',      icon: 'receipt',       i18nKey: 'caisse',      alwaysOn: false },
];

/* ----------------------- Tunisian governorates ----------------------- */

/** The 24 Tunisian governorates (wilayas). ISO 3166-2:TN codes.
 *  Used for the company-address dropdown in the onboarding wizard and
 *  reusable on client/supplier address forms. */
export interface TunisianGovernorate {
  /** ISO code, e.g. 'TN-71' for Tunis. */
  code: string;
  nameFr: string;
  nameAr: string;
}

export const TUNISIAN_GOVERNORATES: readonly TunisianGovernorate[] = [
  { code: 'TN-11', nameFr: 'Tunis',          nameAr: 'تونس' },
  { code: 'TN-12', nameFr: 'Ariana',         nameAr: 'أريانة' },
  { code: 'TN-13', nameFr: 'Ben Arous',      nameAr: 'بن عروس' },
  { code: 'TN-14', nameFr: 'Manouba',        nameAr: 'منوبة' },
  { code: 'TN-21', nameFr: 'Nabeul',         nameAr: 'نابل' },
  { code: 'TN-22', nameFr: 'Zaghouan',       nameAr: 'زغوان' },
  { code: 'TN-23', nameFr: 'Bizerte',        nameAr: 'بنزرت' },
  { code: 'TN-31', nameFr: 'Béja',           nameAr: 'باجة' },
  { code: 'TN-32', nameFr: 'Jendouba',       nameAr: 'جندوبة' },
  { code: 'TN-33', nameFr: 'Kef',            nameAr: 'الكاف' },
  { code: 'TN-34', nameFr: 'Siliana',        nameAr: 'سليانة' },
  { code: 'TN-41', nameFr: 'Kairouan',       nameAr: 'القيروان' },
  { code: 'TN-42', nameFr: 'Kasserine',      nameAr: 'القصرين' },
  { code: 'TN-43', nameFr: 'Sidi Bouzid',    nameAr: 'سيدي بوزيد' },
  { code: 'TN-51', nameFr: 'Sousse',         nameAr: 'سوسة' },
  { code: 'TN-52', nameFr: 'Monastir',       nameAr: 'المنستير' },
  { code: 'TN-53', nameFr: 'Mahdia',         nameAr: 'المهدية' },
  { code: 'TN-61', nameFr: 'Sfax',           nameAr: 'صفاقس' },
  { code: 'TN-71', nameFr: 'Gafsa',          nameAr: 'قفصة' },
  { code: 'TN-72', nameFr: 'Tozeur',         nameAr: 'توزر' },
  { code: 'TN-73', nameFr: 'Kébili',         nameAr: 'قبلي' },
  { code: 'TN-81', nameFr: 'Gabès',          nameAr: 'قابس' },
  { code: 'TN-82', nameFr: 'Médenine',       nameAr: 'مدنين' },
  { code: 'TN-83', nameFr: 'Tataouine',      nameAr: 'تطاوين' },
];

/* ----------------------- Activity sectors ----------------------- */

/** Common activity sectors for Tunisian SMBs. Free-text fallback always
 *  available — users can also type their own sector. */
export const ACTIVITY_SECTORS_I18N: readonly string[] = [
  'retail',
  'wholesale',
  'manufacturing',
  'construction',
  'food_beverage',
  'agriculture',
  'transport_logistics',
  'it_services',
  'consulting',
  'health',
  'education',
  'hospitality',
  'beauty_personal_care',
  'automotive',
  'real_estate',
  'other',
] as const;

export type ActivitySectorKey = (typeof ACTIVITY_SECTORS_I18N)[number];

/* ----------------------- Wizard payload shape ----------------------- */

/** Shape passed from the wizard UI all the way down to the
 *  `create_tenant_for_current_user` RPC. All fields except `name` and
 *  `subdomain` are optional. */
export interface OnboardingPayload {
  // Required basics
  name: string;
  subdomain: string;

  // Optional company details (free-form except governorate)
  legalName?: string | null;
  mf?: string | null;            // Matricule fiscal
  registrationCode?: string | null; // RC
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  governorateCode?: string | null;
  activitySector?: string | null;
  rib?: string | null;
  bankName?: string | null;

  /** Module keys to enable. `sales` is always included server-side regardless. */
  enabledModules: readonly ModuleKey[];
}
